// JSON-RPC client for tool execution and the tool catalog.
//
// The landing chat is anonymous: requests go to same-origin /rpc with no
// Authorization header. The gateway's OptionalAuth middleware passes the
// request through, downstream middleware (CULimiter, BalanceGate, x402)
// no-ops on nil KeyContext, and only CapRead tools are visible to anonymous
// callers — which is exactly what the chat needs (docs_search,
// platform_listModels, platform_getAttestationQuote).

import type { ToolDef, DocsSearchResult } from './types.js';
import { dbg, dbgErr } from './debug.js';

/** JSON-RPC 2.0 response envelope. */
interface RPCResponse {
  jsonrpc: string;
  id: string;
  result?: { content: unknown; usedCUMilli?: number };
  error?: { code: number; message: string; data?: { errorCode?: string } };
}

/** Result of a tool execution with timing and optional error. */
export interface ToolExecResult {
  content: unknown;
  elapsedMs: number;
  error?: string;
}

/**
 * Execute a tool call via JSON-RPC against the same-origin /rpc endpoint.
 *
 * Failures (network errors, non-JSON responses, JSON-RPC errors) are
 * returned as `error` strings rather than thrown so the orchestrator can
 * pass them back to the LLM as tool output instead of aborting the loop.
 */
export async function executeToolCall(
  tool: string,
  params: Record<string, unknown>,
): Promise<ToolExecResult> {
  const start = performance.now();
  dbg('rpc tools.call ->', { tool, params });
  try {
    const resp = await fetch('/rpc', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: crypto.randomUUID(),
        method: 'tools.call',
        params: { tool, params },
      }),
    });
    const elapsed = performance.now() - start;
    const json = (await resp.json()) as RPCResponse;
    if (json.error) {
      const code = json.error.data?.errorCode ?? `RPC_${json.error.code}`;
      dbgErr('rpc tools.call <- error', {
        tool,
        elapsedMs: Math.round(elapsed),
        httpStatus: resp.status,
        code,
        message: json.error.message,
      });
      return {
        content: null,
        elapsedMs: elapsed,
        error: `${code}: ${json.error.message}`,
      };
    }
    dbg('rpc tools.call <- ok', {
      tool,
      elapsedMs: Math.round(elapsed),
      httpStatus: resp.status,
      contentType: Array.isArray(json.result?.content)
        ? `array[${json.result?.content.length}]`
        : typeof json.result?.content,
    });
    return { content: json.result?.content ?? null, elapsedMs: elapsed };
  } catch (err) {
    const elapsed = performance.now() - start;
    dbgErr('rpc tools.call <- threw', { tool, elapsedMs: Math.round(elapsed), err });
    return {
      content: null,
      elapsedMs: elapsed,
      error: err instanceof Error ? err.message : 'Network error',
    };
  }
}

/** Fetch the available tool catalog via JSON-RPC tools.list. */
export async function listTools(): Promise<ToolDef[]> {
  const start = performance.now();
  dbg('rpc tools.list ->');
  try {
    const resp = await fetch('/rpc', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: crypto.randomUUID(),
        method: 'tools.list',
        params: {},
      }),
    });
    const elapsed = performance.now() - start;
    const json = (await resp.json()) as {
      result?: { tools?: Array<{ name: string; description?: string; inputSchema?: unknown }> };
      error?: { code?: number; message?: string };
    };
    if (json.error || !json.result?.tools) {
      dbgErr('rpc tools.list <- empty/error', {
        elapsedMs: Math.round(elapsed),
        httpStatus: resp.status,
        error: json.error,
        hasResult: !!json.result,
        hasTools: !!json.result?.tools,
        toolsLen: json.result?.tools?.length ?? 0,
      });
      return [];
    }
    const tools = json.result.tools.map((t) => ({
      name: t.name,
      description: t.description ?? '',
      inputSchema: t.inputSchema,
    }));
    dbg('rpc tools.list <- ok', {
      elapsedMs: Math.round(elapsed),
      httpStatus: resp.status,
      count: tools.length,
      names: tools.map((t) => t.name),
    });
    return tools;
  } catch (err) {
    const elapsed = performance.now() - start;
    dbgErr('rpc tools.list <- threw', { elapsedMs: Math.round(elapsed), err });
    return [];
  }
}

/**
 * Assess retrieval confidence from the top BM25 score in a docs_search
 * result set. The threshold is empirical and lives in config.ts.
 */
export function assessConfidence(
  results: DocsSearchResult[],
  threshold: number,
): 'high' | 'low' | 'none' {
  if (results.length === 0) return 'none';
  return results[0].score >= threshold ? 'high' : 'low';
}
